/**
 * PageHeader (Compact)
 *
 * Komponen header halaman yang terpusat untuk seluruh halaman Booku V2.
 *
 * Pattern compact density:
 * - Title satu baris (font-size-xl), inline dengan tooltip ⓘ di kanan
 * - Subtitle/deskripsi disampaikan via tooltip (hover ikon ⓘ), bukan baris terpisah
 * - Action buttons di kanan (header-right)
 *
 * Pengganti pattern lama:
 *   <div className="content-header">
 *     <div className="content-header-left">
 *       <h1 className="content-title">Title</h1>
 *       <p className="content-subtitle">Subtitle</p>
 *     </div>
 *     <div className="content-header-right">{actions}</div>
 *   </div>
 *
 * Pemakaian baru:
 *   <PageHeader
 *     title="Buku Besar - Kas Mahani"
 *     description="General Ledger - daftar transaksi per akun"
 *     actions={<><Button>Refresh</Button><Button>Export</Button></>}
 *   />
 *
 * @see .claude/docs/Booku v2/ui-patterns/compact-density-pattern.md
 * @see .claude/docs/Booku v2/ui-patterns/page-layout-pattern.md
 */

import type { ReactNode } from 'react';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

interface PageHeaderProps {
  /** Judul halaman (1 baris, akan terpotong dengan ellipsis bila terlalu panjang) */
  title: ReactNode;
  /**
   * Deskripsi singkat halaman.
   * Akan ditampilkan sebagai tooltip pada ikon ⓘ di sebelah kanan title.
   * Jika tidak diisi, ikon tooltip tidak ditampilkan.
   */
  description?: ReactNode;
  /** Tombol aksi di sisi kanan header (Refresh, Export, Tambah, dll.) */
  actions?: ReactNode;
  /**
   * Posisi `actions`:
   * - `'right'` (default) — di sisi kanan header, terpisah dari title.
   * - `'left'`            — inline di samping title (rata kiri).
   *
   * `'left'` cocok untuk halaman dengan banyak tombol aksi yang harus selalu
   * terlihat di viewport — saat layar sangat lebar, posisi rata kanan akan
   * menjauh dari title sehingga sulit dijangkau visual. Pertama kali dipakai
   * di Daftar Penyusutan Asset Tetap (sesi 24-04-2026).
   */
  actionsPosition?: 'left' | 'right';
}

export function PageHeader({
  title,
  description,
  actions,
  actionsPosition = 'right',
}: PageHeaderProps) {
  const inlineActions = actionsPosition === 'left' && actions;
  const rightActions = actionsPosition === 'right' && actions;

  return (
    <div className="content-header">
      <div
        className="content-header-left"
        style={inlineActions ? { flex: '1 1 auto', flexWrap: 'wrap' } : undefined}
      >
        <h1 className="content-title">{title}</h1>
        {description && (
          <Tooltip title={description} placement="right">
            <span className="content-title-info" aria-label="Info">
              <InfoCircleOutlined />
            </span>
          </Tooltip>
        )}
        {inlineActions && (
          <div
            className="content-header-actions-inline"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              marginLeft: 16,
            }}
          >
            {actions}
          </div>
        )}
      </div>
      {rightActions && <div className="content-header-right">{actions}</div>}
    </div>
  );
}
